import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import moment from 'moment'
import axios from '../plugins/axios'
import Calendar from 'react-calendar'
import { useRecoilState } from 'recoil'
import { isFromSearchState, selectBedInfoState } from '../plugins/recoil'
import { useTranslation } from 'react-i18next'

import {ReactComponent as IconArrowRight} from '../assets/icons/ic-arrow-right.svg'
import {ReactComponent as IconArrowLeft} from '../assets/icons/ic-arrow-left.svg'
import {ReactComponent as IconClose} from '../assets/icons/ic-close.svg'
import { set } from 'lodash'

const ReviewAreaDateModal = forwardRef(({ zoom, isAlarmReview, handleClose, handleSearch}, ref) => {
    const { t } = useTranslation()
    const localRef = useRef(null)
    const [calendarAlarms, setCalendarAlarms] = useState([])
    const [isFromSearch] = useRecoilState(isFromSearchState)
    const [selectBedInfo] = useRecoilState(selectBedInfoState)
    const [editStartDate, setEditStartDate] = useState(new Date())
    const [editEndDate, setEditEndDate] = useState(new Date())    
    const [editDate, setEditDate] = useState(new Date())
    const [editHour, setEditHour] = useState(0);
    const [editMinute, setEditMinute] = useState(0);
    const [activeStartDate, setActiveStartDate] = useState(new Date())

    const TileContent = ({ activeStartDate, date, view }) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        const alarm = calendarAlarms.find(alarm => alarm.date === formattedDate);
        const isSelect = moment(editDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')
        
        if (alarm) alarm.priority.sort((a, b) => b - a)
        if (isSelect) {
            return (
                <>                    
                    <div className="absolute bg-gradient-to-b from-[#3182F7] to-[#1562D1]" style={{ top: 3, width: 32, height: 32, borderRadius: 6, zIndex: -1 }} />
                    {
                        alarm &&
                        <div className="flex items-center justify-center">
                            {
                                alarm.priority.map((priority, index) => {
                                    let bgColor = priority === 2 ? '#FF002E' : priority === 4 ? '#FFFFFF' : '#FFF500';
                                    return <div className="w-[4px] h-[4px] mr-1" key={`${date}-alarm-${index}`} style={{ borderRadius: 3, backgroundColor: bgColor }} />
                                })
                            }
                        </div>
                    }
                </>
            )
        } else {
            return (
                <>
                    {
                        alarm &&
                        <div className="flex items-center justify-center">
                            {
                                alarm.priority.map((priority, index) => {
                                    let bgColor = priority === 2 ? '#FF002E' : priority === 4 ? '#FFFFFF' : '#FFF500';
                                    return <div className="w-[4px] h-[4px] mr-1" key={`${date}-alarm-${index}`} style={{ borderRadius: 3, backgroundColor: bgColor }} />
                                })
                            }
                        </div>
                    }
                </>
            )
        }
    }

    const tileDisabled = ({ date, view  }) => {
        if (isFromSearch) {
            const isPastOrFuture = moment(date).isBefore(moment(selectBedInfo.startTime)) || moment(date).isAfter(moment(selectBedInfo.endTime))
            return isPastOrFuture
        } else {
            const isFuture = moment(date).isAfter(moment())
            return isFuture;
        }
    };

    const getCalendarAlarms = async (val) => {
        try {
            let serialsToQuery = [selectBedInfo.serial]; // 기본값
            if (isFromSearch && selectBedInfo.deviceHistory && selectBedInfo.deviceHistory.length > 0) {
                // 해당 월의 시작과 끝 시간
                const startTime = moment(val).startOf('month');
                const endTime = moment(val).endOf('month');
                const activeSerials = new Set();

                // deviceHistory를 시간순으로 정렬
                const sortedHistory = [...selectBedInfo.deviceHistory].sort((a, b) => 
                    moment(a.createdAt).diff(moment(b.createdAt))
                );

                let currentSerial = null;
                let currentAdmittedTime = null;

                for (const history of sortedHistory) {
                    const historyTime = moment(history.createdAt);

                    if (history.action === 'ADMITTED') {
                        currentSerial = history.serial;
                        currentAdmittedTime = historyTime;
                    } else if (history.action === 'UNASSIGNED' && currentSerial) {
                        // ADMITTED ~ UNASSIGNED 기간이 해당 월과 겹치는지 확인
                        const admittedTime = currentAdmittedTime;
                        const unassignedTime = historyTime;
                        
                        // 기간이 겹치는 경우 serial 추가
                        if (admittedTime.isBefore(endTime) && unassignedTime.isAfter(startTime)) {
                            activeSerials.add(currentSerial);
                        }
                        
                        currentSerial = null;
                        currentAdmittedTime = null;
                    }
                }

                // 마지막 ADMITTED 상태가 종료되지 않은 경우 (현재 사용 중)
                if (currentSerial && currentAdmittedTime) {
                    // ADMITTED 시간이 해당 월 종료 시간보다 이전이면 해당 serial 포함
                    if (currentAdmittedTime.isBefore(endTime)) {
                        activeSerials.add(currentSerial);
                    }
                }

                serialsToQuery = activeSerials.size > 0 ? Array.from(activeSerials) : [selectBedInfo.serial];
            }

            // 각 serial에 대해 API 호출
            const calendarPromises = serialsToQuery.map(serial => 
                axios.get(`/trend/alarm/event/calendar?serialNo=${serial}&startTime=${moment(val).startOf('month').format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment(val).endOf('month').format('YYYY-MM-DDTHH:mm:ss')}Z`)
            );

            const responses = await Promise.all(calendarPromises);
            
            // 모든 응답을 하나로 합치기
            const allCalendarEvents = [];
            responses.forEach(res => {
                if (res.code == '200' && res.data.events) {
                    allCalendarEvents.push(...res.data.events);
                }
            });

            // 날짜별로 그룹화하고 중복 제거
            const eventsByDate = {};
            allCalendarEvents.forEach(event => {
                if (!eventsByDate[event.date]) {
                    eventsByDate[event.date] = { date: event.date, priority: new Set() };
                }
                event.priority.forEach(priority => eventsByDate[event.date].priority.add(priority));
            });

            // Set을 배열로 변환
            const mergedEvents = Object.values(eventsByDate).map(event => ({
                date: event.date,
                priority: Array.from(event.priority).sort((a, b) => b - a)
            }));

            setCalendarAlarms(mergedEvents);
        } catch (err) {
            console.warn(err)
            setCalendarAlarms([])
        }
    }

    const handleChangeTimeInput = (type, e) => {
        const inputValue = e.target.value;
    
        // 숫자가 두 자리 이하인지 확인
        switch(type) {
            case 'h':
                if (/^(2[0-3]|[01]?[0-9])$/.test(inputValue) || inputValue === '') {
                    setEditHour(inputValue ? parseInt(inputValue) : '');
                }
                break;
            case 'm':
                if (/^(5[0-9]|[0-4]?[0-9])$/.test(inputValue) || inputValue === '') {
                    setEditMinute(inputValue ? parseInt(inputValue) : '');
                }
                break;
        }
    };

    const handleChkEmpty = (type,e) => {
        const inputValue = e.target.value;

        switch(type) {
            case 'h':
                setEditHour(inputValue ? parseInt(inputValue) : '00' );
                break;
            case 'm':
                setEditMinute(inputValue ? parseInt(inputValue) : '00');
                break;
        }
    }

    const handleKeyDown = (e) => {
        // 'e', '+', '-' 키 입력 방지
        if (['e', '+', '-', '.', ','].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleClickSearch = () => {
        if (isApliable()) {
            handleSearch(editStartDate, editEndDate)
        }
    }

    const handleCalendarMove = (direction) => {
        const newEditDate = direction === 'prev' ? new Date(moment(editDate).subtract(1, 'month')) : new Date(moment(editDate).add(1, 'month'));
        getCalendarAlarms(newEditDate)
        setEditDate(newEditDate);
    }

    const isApliable = () => {
        if (isFromSearch) {
            return (moment(editStartDate).isSameOrAfter(moment(selectBedInfo.startTime)) && moment(editEndDate).isSameOrBefore(moment(selectBedInfo.endTime)))
        } else {
            return !(moment().isBefore(moment(editEndDate)))
        }
    }

    //달력 변경
    useEffect(() => {
        setActiveStartDate(editDate)
        const newDate = moment(editDate)
        newDate.set('hour', editHour)
        newDate.set('minute', editMinute)
        setEditEndDate(new Date(moment(newDate)))
        if (isAlarmReview) {
            setEditStartDate(new Date(moment(newDate).subtract(1, 'days')))
        } else {
            const newStartDate = new Date(moment(newDate).add(zoom, 'hours')) 
            setEditStartDate(newStartDate); 
        }
    }, [editDate])

    //시간 체크
    useEffect(() => {
        const newDate = moment(editEndDate)  
        newDate.set('hour', editHour)
        newDate.set('minute', editMinute)
        setEditEndDate(new Date(newDate))
        if (isAlarmReview) {
            setEditStartDate(new Date(moment(newDate).subtract(1, 'days')))
        } else {
            const newStartDate = new Date(moment(newDate).add(zoom, 'hours')) 
            setEditStartDate(newStartDate); 
        }
    }, [editHour, editMinute ])    

    useImperativeHandle(ref, () => ({
        init: (val) => {
            getCalendarAlarms(val)
            setEditEndDate(val)
            setEditDate(val)
            setEditHour(moment(val).get('hour'))
            setEditMinute(moment(val).get('minute'))
        },
        getDom: () => localRef.current
    }));

    return (
        // <div ref={localRef} className="date-select-modal absolute w-[536px] h-[280px] top-[32px] bg-[#DEDEDE]" style={{ top: isAlarmReview ? 32 : -290, left: isAlarmReview ? 'calc(50% - 147px)' : 32, borderWidth: 1, borderRadius: 8, borderColor: '#5E5E5E', boxShadow: '0px 12px 12px 0px #00000099', zIndex: 11, width: isAlarmReview ? 284 : 536, height: isAlarmReview ? 331 : 280, zIndex: 10000 }}>            
        <div ref={localRef} className="date-select-modal absolute w-[536px] top-[32px] bg-[#1F1F1F]" style={{ top: isAlarmReview ? 32 : -290, left: isAlarmReview ? 'calc(50% - 147px)' : 32, borderWidth: 1, borderRadius: 8, borderColor: '#5E5E5E', boxShadow: '0px 12px 12px 0px #00000099', zIndex: 11, width: 536, zIndex: 10000 }}>            
            <div className="flex items-center bg-[#353535] mb-1" style={{ padding: '8px 20px', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                <div className="text-[#DEDEDE]" style={{ fontSize: 16 }}>{t('modal.dateSearch')}</div>
                <IconClose className="ml-auto cursor-pointer" style={{ marginRight: -8 }} onClick={() => { handleClose() }}/>
            </div>
            <div className="flex px-1">
                <div className="flex flex-col flex-1 px-5" style={{  }}>
                    <div className="relative flex items-center justify-between mb-4 mt-3 font-white">
                        <IconArrowLeft className="cursor-pointer" onClick={() => { handleCalendarMove('prev') }}/>
                        <div className="flex items-center" style={{ fontWeight: 500, fontSize: 16, lineHeight: '17.6px' }}>
                            <div className="">{ moment(editDate).format('MMMM YYYY') }</div>
                        </div>
                        <IconArrowRight className="cursor-pointer" onClick={() => { handleCalendarMove('next') }}/>
                    </div>
                    <Calendar
                        calendarType={'gregory'}
                        showNavigation={false}
                        tileClassName={['relative', 'flex', 'flex-col', 'justify-center', 'font-white']}
                        tileContent={TileContent}
                        tileDisabled={tileDisabled}
                        formatDay={(locale, date) => moment(date).format('D')}
                        formatShortWeekday={(locale, date) =>  moment(date).format('ddd').charAt(0)}
                        locale={'en'}
                        prev2Label={null}
                        next2Label={null}
                        showNeighboringMonth={false}
                        onChange={setEditDate}
                        value={editDate}
                        maxDate={new Date()}
                        activeStartDate={activeStartDate}
                        onActiveStartDateChange={({activeStartDate}) => setActiveStartDate(activeStartDate)}
                    />
                </div>
                <div className="flex flex-col flex-1 pb-6 pr-6 gap-[6px]" style={{ maxWidth: '50%' }}>
                    <div className="mt-3 text-[#F4F4F4]" style={{ fontWeight: 300, fontSize: 14, lineHeight: '15.4px' }}>{t('trend.time')} {'('}{moment(editEndDate).format('YYYY-MM-DD')}{')'}</div>
                    <div className="flex items-center text-[#F4F4F4]">
                        <input type='number' value={editHour} onChange={(e) => {handleChangeTimeInput('h', e)}} onBlur={(e) => {handleChkEmpty('h',e)}} onKeyDown={handleKeyDown} className="flex-1 time-input" maxLength={2}/>
                            <div className="mx-2">:</div>
                        <input type='number' value={editMinute} onChange={(e) => {handleChangeTimeInput('m', e)}} onBlur={(e) => {handleChkEmpty('m',e)}} onKeyDown={handleKeyDown} className="flex-1 time-input"  maxLength={2}/>
                    </div>
                    <div className="font-light text-[#9F9F9F]" style={{ fontSize: 13 }}>{t('modal.dateSearchMessage')}​</div>
                    <div className="mt-auto ml-auto flex items-center gap-3" style={{ fontWeight: 500, fontSize: 14, lineHeight: '15.4px' }}>
                            <div className="text-[#9F9F9F] cursor-pointer" onClick={() => { handleClose() }}>{t('modal.cancel')}</div>
                        <div className="text-[#3172F7] cursor-pointer" onClick={() => { handleClickSearch() }} style={{ opacity: isApliable() ? 1 : 0.3 }}>{t('modal.ok')}</div>
                    </div>
                </div>
            </div>
        </div>
    )
})

export default ReviewAreaDateModal